/**
 * Minimal session shape the UI cares about — `userId` matches the
 * app-level `users.id`. */
export interface AuthSession {
  userId: string;
  email: string;
}

export type AuthStateListener = (session: AuthSession | null) => void;

/**
 * Returned by `signInWithPassword` when the password is correct but the
 * mandatory two-factor step still has to be completed (the company-auth
 * backend is 2FA-by-design — `/login/` never issues a session on its own).
 */
export interface TwoFaChallenge {
  status: "two_fa_required";
  /** Proof that the password step succeeded — passed back to
   * `verifyTwoFa`. Lasts 5 minutes and allows 5 codes (company-auth#30). */
  ticket: string;
  /** TOTP-enrollment QR code as a data URL. Non-null only the first time
   * a user signs in, before they've added Vidario to an authenticator
   * app; afterwards the user just types the current 6-digit code. */
  qrCode: string | null;
}

export type SignInResult =
  | { status: "authenticated"; session: AuthSession }
  | TwoFaChallenge;

/** The contract every auth backend satisfies. `company-auth` satisfies it
 * via a two-step adapter; `MockAuthClient` satisfies it locally for
 * dev/tests (and never issues a 2FA challenge). All methods are async,
 * all errors are thrown as `AuthError`. */
export interface AuthClient {
  getSession(): Promise<AuthSession | null>;
  /** Step 1 — verify credentials. Resolves either to a ready session
   * (remembered device) or to a 2FA challenge. */
  signInWithPassword(email: string, password: string): Promise<SignInResult>;
  /** Step 2 — verify the TOTP code with the ticket from a `TwoFaChallenge`.
   * `rememberDevice` skips 2FA on this browser for 30 days. */
  verifyTwoFa(
    ticket: string,
    code: string,
    options?: { rememberDevice?: boolean },
  ): Promise<AuthSession>;
  signOut(): Promise<void>;
  resetPasswordForEmail(email: string, options?: { redirectTo?: string }): Promise<void>;
  updatePassword(newPassword: string): Promise<void>;
  /** Subscribe to session changes. Returns an unsubscribe function. */
  onAuthStateChange(listener: AuthStateListener): () => void;
}

/** Thrown by every AuthClient operation on any failure. `code` is a
 * stable identifier so the UI can localise without parsing messages. */
export class AuthError extends Error {
  public readonly code: AuthErrorCode;
  public override readonly cause: unknown;
  constructor(code: AuthErrorCode, message: string, cause?: unknown) {
    super(message);
    this.name = "AuthError";
    this.code = code;
    this.cause = cause;
  }
}

export type AuthErrorCode =
  | "invalid_credentials"
  | "invalid_2fa_code"
  /** The 2FA challenge from step 1 timed out; the user must start over. */
  | "challenge_expired"
  /** An invitation or password-reset link expired or is invalid (#21). */
  | "link_expired"
  /** The new password broke a password rule; the message says which. */
  | "weak_password"
  | "email_not_confirmed"
  | "rate_limited"
  | "network"
  | "not_authenticated"
  | "unknown";
